import { Box, VStack, HStack, Stack, Text, Button, Spinner, Badge, Input } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom'
import { ResponsiveTable, type ResponsiveColumn } from '../shared'
import type { PaginatedBusinessEmployeesResult, BusinessDetailEmployee } from '../../types/business'

interface BusinessEmployeeTableProps {
  totalEmployeeCount: number
  empData: PaginatedBusinessEmployeesResult | undefined
  empLoading: boolean
  setEmpPage: (updater: (prev: number) => number) => void
  searchInput: string
  onSearchInputChange: (value: string) => void
  onSubmitSearch: () => void
}

export function BusinessEmployeeTable({
  totalEmployeeCount, empData, empLoading, setEmpPage,
  searchInput, onSearchInputChange, onSubmitSearch,
}: BusinessEmployeeTableProps) {
  const navigate = useNavigate()
  const empTotalPages = empData ? Math.ceil(empData.totalCount / empData.pageSize) : 0

  const employeeColumns: ResponsiveColumn<BusinessDetailEmployee>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (emp) => <Text fontWeight="medium" fontSize="sm">{emp.contactName}</Text>,
    },
    {
      header: 'Worksite',
      mobileRole: 'meta',
      cell: (emp) => <Text fontSize="sm" color="gray.600">{emp.worksiteName}</Text>,
    },
    {
      header: 'Position',
      mobileRole: 'meta',
      cell: (emp) => <Text fontSize="sm">{emp.position || '---'}</Text>,
    },
    {
      header: 'Department',
      mobileRole: 'meta',
      cell: (emp) => <Text fontSize="sm">{emp.department || '---'}</Text>,
    },
    {
      header: 'Primary',
      mobileRole: 'badge',
      cell: (emp) => emp.isPrimary ? <Badge colorPalette="green" fontSize="xs">Primary</Badge> : null,
    },
    {
      header: 'Union',
      mobileRole: 'meta',
      cell: (emp) => emp.unionName ? <Badge colorPalette="purple" fontSize="xs">{emp.unionName}</Badge> : null,
    },
  ]

  return (
    <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
      <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} mb={4} gap={2}>
        <Text fontSize="lg" fontWeight="semibold">
          Employees ({totalEmployeeCount})
        </Text>
        <HStack gap={2}>
          <Input
            size="sm"
            minH="44px"
            maxW={{ base: 'full', md: '260px' }}
            placeholder="Search name or position..."
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSubmitSearch() }}
          />
          <Button size="sm" minH="44px" onClick={onSubmitSearch}>Search</Button>
        </HStack>
      </Stack>
      {empLoading && (
        <VStack py={6}>
          <Spinner size="md" color="accent" />
        </VStack>
      )}
      {!empLoading && empData && empData.items.length === 0 && (
        <Text color="gray.500" fontSize="sm">
          {searchInput ? 'No employees match your search.' : 'No employees found.'}
        </Text>
      )}
      {!empLoading && empData && empData.items.length > 0 && (
        <>
          <ResponsiveTable
            bare
            columns={employeeColumns}
            rows={empData.items}
            keyFn={(emp) => emp.employmentId}
            onRowClick={(emp) => navigate(`/dashboard/contacts/${emp.contactId}`)}
          />

          {empTotalPages > 1 && (
            <HStack justify="space-between" px={2} mt={4}>
              <Text fontSize="sm" color="gray.500">
                Showing {((empData.page - 1) * empData.pageSize) + 1}--{Math.min(empData.page * empData.pageSize, empData.totalCount)} of {empData.totalCount}
              </Text>
              <HStack gap={2}>
                <Button size="sm" variant="outline" disabled={empData.page <= 1}
                  onClick={() => setEmpPage(prev => prev - 1)}
                >Previous</Button>
                <Text fontSize="sm" color="gray.600">Page {empData.page} of {empTotalPages}</Text>
                <Button size="sm" variant="outline" disabled={empData.page >= empTotalPages}
                  onClick={() => setEmpPage(prev => prev + 1)}
                >Next</Button>
              </HStack>
            </HStack>
          )}
        </>
      )}
    </Box>
  );
}
